export interface KPI {
  id: string
  name: string
  description: string | null
  target_value: number
  current_value: number
  unit: string
  frequency: string
  category: string
  status: string
  priority: string
  start_date: string | null
  end_date: string | null
  // Enterprise Measurement Configuration
  measurement_direction?: 'higher_is_better' | 'lower_is_better'
  measurement_mode?: 'numeric' | 'percentage' | 'rating' | 'binary'
}

export interface PendingObjective {
  id: number
  title: string
  description: string
  role_type: string
  functional_area: string
  status: string
  progress: number
  start_date: string | null
  end_date: string | null
  kpa: {
    id: number
    name: string
    display_name: string
  } | null
  pillar: {
    id: number
    name: string
    color_code: string
  } | null
  strategic_objective: {
    id: number
    title: string
  } | null
  // New 5-level strategic framework fields
  strategic_objective_id?: number | null
  strategic_initiative_id?: number | null
  operational_initiative_id?: number | null
  kpis: KPI[]
  kpi_count: number
  created_at: string | null
  was_previously_active?: boolean
  // Persisted AI SMART analysis (overallScore + per-criterion {score, feedback, suggestions})
  smart_analysis?: any
}

export interface RejectedObjective extends PendingObjective {
  rejection_reason: string | null
  rejected_at: string | null
}

export interface TeamMember {
  employee_id: string
  display_name: string
  job_title: string
  campus: string
  department: string
  objectives: {
    total: number
    onTrack: number
    atRisk: number
    offTrack: number
    completed: number
    pending_approval: number
    progress: number
  }
  pending_objectives: PendingObjective[]
  active_objectives: PendingObjective[]
  rejected_objectives: RejectedObjective[]
}

export interface ObjectiveConfigurationDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  teamMember: TeamMember | null
  userRole?: 'manager' | 'employee'
  inline?: boolean
  initialTab?: 'pending' | 'active' | 'rejected' | 'allocate'
  selectedYear?: number
  onApprove: (objectiveId: number, feedback?: string, edits?: PendingObjective | null, employeeId?: string) => Promise<void>
  // Optional fast-path for "Approve All": approves many objectives with a single
  // refresh at the end instead of a full team reload per objective. When omitted,
  // the dialog falls back to looping onApprove sequentially.
  onApproveBatch?: (
    items: Array<{ id: number; feedback?: string }>,
    employeeId?: string
  ) => Promise<{ approved: number; failed: number }>
  onReject: (objectiveId: number, reason: string) => Promise<void>
  onUpdate: (objectiveId: number, updates: PendingObjective, employeeId?: string) => Promise<void>
  onAllocate: (objectives: any[]) => Promise<void>
  // Fired after objectives were copied in on the server (from the year before or the
  // Objective Library: HR UAT list #27), so the parent sets up the meeting about them, as
  // onAllocate does for objectives it writes itself, and refreshes.
  onObjectivesAdded?: () => Promise<void>
  onObjectivesChange?: () => Promise<void>
  // Fired after the dialog schedules/completes (or auto-completes) a collaborative
  // session, so a parent that keeps its own session list for the approval gate can
  // refresh and avoid blocking approvals against a stale snapshot.
  onSessionChange?: () => void | Promise<void>
}

export const getPillarColor = (pillarName?: string) => {
  if (!pillarName) return 'bg-gray-100 text-gray-800'

  switch (pillarName.toLowerCase()) {
    case 'scale':
      return 'bg-purple-100 text-purple-800'
    case 'quality':
      return 'bg-emerald-100 text-emerald-800'
    case 'culture':
      return 'bg-blue-100 text-blue-800'
    case 'impact':
      return 'bg-orange-100 text-orange-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

export const getFunctionalAreaColor = (area: string) => {
  const colors: Record<string, string> = {
    'operating-financial': 'bg-emerald-100 text-emerald-800',
    'internal-business-processes': 'bg-blue-100 text-blue-800',
    'culture-people': 'bg-purple-100 text-purple-800',
    'customer-stakeholder': 'bg-orange-100 text-orange-800'
  }
  return colors[area] || 'bg-gray-100 text-gray-800'
}

export const formatDate = (dateString: string | null) => {
  if (!dateString) return 'Not set'
  return new Date(dateString).toLocaleDateString('en-ZA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

export const filterObjectives = <T extends PendingObjective>(objectives: T[], searchQuery: string, selectedKPA: string): T[] => {
  return objectives.filter(obj => {
    const matchesSearch = searchQuery === '' ||
      obj.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      obj.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      obj.kpa?.display_name?.toLowerCase().includes(searchQuery.toLowerCase())

    const matchesKPA = selectedKPA === 'all' || obj.kpa?.display_name === selectedKPA

    return matchesSearch && matchesKPA
  })
}
